import {
	Conversation,
	type Figure,
	Figures,
	InShort,
	RelatedPages,
	type Turn,
} from "@marketing/components/answer";
import { Article } from "@marketing/components/article";
import {
	DetailGrid,
	marketingHead,
	PageIntro,
	Section,
	Steps,
} from "@marketing/components/blocks";
import { LatencyTimeline } from "@marketing/components/LatencyTimeline";
import { CallVisual } from "@marketing/components/visuals";
import { Closing } from "@marketing/home/Closing";
import { Faq } from "@marketing/home/Faq";
import { CallPlayer } from "@marketing/home/Voice";
import { createFileRoute } from "@tanstack/react-router";

const UPDATED = "2026-11-05";

const FAQ = [
	{
		q: "What is an AI voice agent for phone orders?",
		a: "An assistant that answers a store's calls, understands what the caller wants to buy, searches the catalog, builds the basket and reads it back. Hephon Commerce does this in Arabic and English for supermarkets and grocery retailers, and the caller confirms the order on the keypad.",
	},
	{
		q: "Which languages does the voice agent speak?",
		a: "Saudi, Gulf and Lebanese Arabic as people speak them, English, and more than 30 other languages. It answers in the dialect you choose for your store, in a voice picked for it; a Saudi voice is available.",
	},
	{
		q: "How fast does it answer?",
		a: "In under a second, from the caller's last word to the assistant's first. It hears the caller while they speak and starts the product search before they have finished, so most of the work is done when they stop.",
	},
	{
		q: "Can it take an order over the phone?",
		a: "It builds the basket during the call, reads it back, and the caller confirms on the keypad by pressing 1. A spoken yes alone never places an order.",
	},
	{
		q: "Where can shoppers call it?",
		a: "On your WhatsApp Business number, and with live voice inside your app or on your hosted chat page. If you want an existing phone line to reach it, we scope that connection with your team.",
	},
	{
		q: "Can a caller speak to a person?",
		a: "Yes. A caller can always be handed to a person on your team, and the call and its transcript are in your inbox.",
	},
	{
		q: "Does it talk over callers or react to background noise?",
		a: "It is built not to. It drops sound that has no speech in it, and it holds a sentence that stopped half way until the caller finishes, instead of answering early.",
	},
	{
		q: "How is voice ordering priced?",
		a: "Pricing is a proposal per retailer and depends on your stores and the channels you use. Book a demo and we will send you a proposal.",
	},
];

const CALL: Turn[] = [
	{
		from: "shopper",
		text: "السلام عليكم، أبي أطلب",
		en: "Hello, I'd like to order.",
	},
	{
		from: "assistant",
		text: "وعليكم السلام، حياك. وش تحتاج اليوم؟",
		en: "Hello, welcome. What do you need today?",
		note: "Answers in Saudi Arabic with a Saudi voice.",
	},
	{
		from: "shopper",
		text: "أبي دجاج كيلوين و… رز بسمتي خمسة كيلو",
		en: "I want two kilos of chicken and… five kilos of basmati rice.",
		note: "The caller paused mid-sentence. The assistant waited for the rest, and the search started while they were still speaking.",
	},
	{
		from: "assistant",
		text: "أضفت الدجاج كيلوين والرز البسمتي خمسة كيلو. تبي شي ثاني؟",
		en: "I added two kilos of chicken and five kilos of basmati rice. Anything else?",
		note: "A clean basket change is confirmed with a fixed line in the caller's language.",
	},
	{
		from: "shopper",
		text: "لا، بس كذا",
		en: "No, that's all.",
	},
	{
		from: "assistant",
		text: "طلبك: دجاج كيلوين، ورز بسمتي خمسة كيلو. إذا تبي تأكد الطلب اضغط واحد.",
		en: "Your order: two kilos of chicken and five kilos of basmati rice. To confirm, press 1.",
		note: "The basket is read back before anything is ordered.",
	},
	{
		from: "shopper",
		text: "(presses 1)",
	},
	{
		from: "assistant",
		text: "تم تأكيد طلبك. يعطيك العافية.",
		en: "Your order is confirmed. Thank you.",
		note: "Only the keypad places the order. A spoken yes would not.",
	},
];

const QUALITIES = [
	{
		title: "Your shoppers' language",
		text: "Saudi, Gulf and Lebanese Arabic, heard and spoken the way people talk, plus English and more than 30 other languages.",
	},
	{
		title: "Hears while they speak",
		text: "Speech is transcribed as it comes in, and a pause starts the product search before the caller has finished.",
	},
	{
		title: "Under a second to answer",
		text: "From the caller's last word to the assistant's first.",
	},
	{
		title: "Knows when they are done",
		text: "A sentence that stops half way is held for the rest, and background noise is ignored, so it does not talk over people.",
	},
	{
		title: "WhatsApp calls",
		text: "Shoppers call your WhatsApp Business number and talk to the same assistant that answers your chats.",
	},
	{
		title: "Live voice in your app",
		text: "A hands-free mode inside your app and on your hosted chat page, with voice notes too.",
	},
];

const FIGURES: Figure[] = [
	{
		value: "Under 1 s",
		label: "From the caller's last word to the assistant's first",
		source: "As stated by Hephon for calls, November 2026.",
	},
	{
		value: "30+",
		label: "Languages it speaks by voice",
		source: "Hephon Commerce voice engines, November 2026.",
	},
	{
		value: "3 channels",
		label: "Phone, WhatsApp and your app share one basket",
		source: "Hephon Commerce product, November 2026.",
	},
];

const STEPS = [
	{
		title: "Connect your catalog",
		text: "The same catalog as your chat, with each branch's prices and stock. Nothing extra to load for voice.",
	},
	{
		title: "Pick the voice and test it",
		text: "Choose the dialect and voice, such as a Saudi voice, and when to hand over. We run test calls on your own products.",
	},
	{
		title: "Turn on calls",
		text: "On your WhatsApp Business number and in your app first; we scope any other phone line with you.",
	},
];

const RELATED = [
	{ href: "/channels/whatsapp", label: "WhatsApp ordering" },
	{ href: "/channels/in-app", label: "In your app" },
	{ href: "/saudi-arabia", label: "For Saudi Arabia" },
	{ href: "/uae", label: "For the UAE" },
	{ href: "/lebanon", label: "Lebanese by voice" },
	{
		href: "/arabic-chatbot-for-ecommerce",
		label: "Arabic chatbot for ecommerce",
	},
];

export const Route = createFileRoute("/_marketing/channels/voice")({
	head: () =>
		marketingHead({
			title: "AI Voice Agent for Phone Orders in Arabic | Hephon Commerce",
			description:
				"AI voice agent for phone orders in Arabic: shoppers order groceries by WhatsApp call or in your app, in Saudi, Gulf or Lebanese Arabic, basket read back.",
			path: "/channels/voice",
			breadcrumb: ["Voice ordering"],
			faq: FAQ,
			updated: UPDATED,
		}),
	component: VoicePage,
});

function VoicePage() {
	return (
		<>
			<PageIntro
				kicker="Your store answers the phone."
				title="An AI voice agent that takes phone orders in Arabic and English"
				lead="Shoppers call and order by voice in Arabic or English. The assistant searches your catalog, fills the basket and reads it back before anything is ordered."
			>
				<CallVisual />
			</PageIntro>

			<InShort updated={UPDATED}>
				<p>
					Hephon Commerce's voice agent takes grocery orders by voice:
					on calls to the retailer's WhatsApp Business number and with
					live voice in its app. It speaks Saudi, Gulf and Lebanese
					Arabic, English and more than 30 other languages.
				</p>
				<p>
					It listens while the caller speaks, searches the retailer's
					live catalog with the branch's prices, and replies in under
					a second. It reads the basket back, and the order is placed
					only when the caller presses 1. A caller can always be
					handed to a person.
				</p>
			</InShort>

			<Section
				id="listen-title"
				tone="night"
				title="Hear it take an order."
				lead="Two sample calls, recorded with the voices the assistant uses."
				center
			>
				<div className="mx-auto max-w-3xl">
					<CallPlayer />
				</div>
			</Section>

			<Section
				id="call-title"
				tone="mist"
				title="A Saudi phone order, line by line."
				lead="What the caller said, what the assistant answered, and what it did in between."
				center
			>
				<Conversation
					turns={CALL}
					caption="A sample call written for this page to show the behaviour. It is not a recording of a real shopper."
				/>
			</Section>

			<Article>
				<h2 id="how">How does AI phone ordering work, step by step?</h2>
				<p>
					A phone order is harder than a chat. The caller does not
					wait for a prompt, pauses in the middle of a sentence, and
					calls from a kitchen or a car. Here is what Hephon Commerce
					does on each call.
				</p>
				<ol>
					<li>
						<strong>It listens before it transcribes.</strong> A
						noise gate checks each clip of sound for speech first. A
						cough, a door or traffic is dropped, so it never answers
						a noise.
					</li>
					<li>
						<strong>It hears the caller as they speak.</strong>{" "}
						Speech is transcribed while it comes in, not after the
						caller stops, in the caller's language and dialect.
					</li>
					<li>
						<strong>It knows when a sentence is finished.</strong> A
						sentence that stopped half way ("I want chicken and…")
						is held and joined to what follows, so it does not talk
						over a caller who is thinking.
					</li>
					<li>
						<strong>It searches while the caller finishes.</strong>{" "}
						At a short pause the product search starts, over the
						same live catalog and the same checks as your chat, with
						the prices of the caller's branch. If the caller keeps
						talking, that work is thrown away and started again.
					</li>
					<li>
						<strong>It confirms each change.</strong> When an item
						is added or removed cleanly, a fixed line in the
						caller's language confirms it. Anything unclear, such as
						an out-of-stock item, goes back to the assistant to ask.
					</li>
					<li>
						<strong>It reads the basket back.</strong> Every item
						and the total, in the caller's language, before anything
						is ordered.
					</li>
					<li>
						<strong>The keypad confirms.</strong> Pressing 1 places
						the order. A spoken yes alone never does.
					</li>
				</ol>
				<p>
					A caller can ask for a person at any point and be handed to
					your team. Every call is in your inbox with its transcript.
				</p>
			</Article>

			<Section
				id="timing-title"
				tone="mist"
				title="What happens in under a second."
				lead="It listens while the caller speaks, so most of the work is done before they stop."
			>
				<LatencyTimeline />
			</Section>

			<Section
				id="qualities-title"
				title="It sounds local, and checks before it orders."
			>
				<DetailGrid items={QUALITIES} />
			</Section>

			<Article tone="mist">
				<h2 id="saudi-voice">Does it speak Saudi Arabic on calls?</h2>
				<p>
					Yes. Gulf Arabic is heard and spoken with ElevenLabs voices,
					and a Saudi voice is available, so a caller in Riyadh or
					Jeddah hears their own dialect rather than formal Arabic.
					Lebanese Arabic is heard and spoken through a Lebanese
					speech engine. The fixed lines on a call, such as
					confirmations and the read-back, follow the store's dialect,
					and what the assistant says follows the language the caller
					uses. If a caller switches to English, so does the
					assistant.
				</p>

				<h2 id="where">Where can shoppers call it?</h2>
				<p>
					On your WhatsApp Business number, where shoppers already
					message you, and with live voice inside your app or on your
					hosted chat page. It is the same assistant that answers your
					chats, with the same basket: a shopper can start on a call
					and finish in the app. WhatsApp voice notes are transcribed
					and answered like typed messages. Whether WhatsApp calls are
					available depends on the country and on Meta's rules for
					business calling; if you want an existing phone line to
					reach the assistant, we scope that with your team.
				</p>

				<h2 id="voice-ordering">
					What is voice ordering, and who is it for?
				</h2>
				<p>
					Voice ordering lets a shopper buy by speaking instead of
					tapping through an app. It suits families who already phone
					their store, older shoppers, people cooking or driving, and
					anyone who finds a long app list slow. For a supermarket it
					turns calls that a person had to take down by hand into
					baskets that arrive in your platform like any other order.
				</p>

				<h2 id="does-not">What it does not do</h2>
				<ul>
					<li>
						It never places an order without keypad 1 after the
						read-back.
					</li>
					<li>
						It does not invent products, prices or delivery times.
						It offers what the caller's branch has.
					</li>
					<li>
						It does not show what your store never sells. Alcohol,
						pork or tobacco are removed on the server, and on
						WhatsApp alcohol and tobacco are always excluded.
					</li>
				</ul>

				<h2 id="not-fit">When Hephon Commerce is not the right fit</h2>
				<ul>
					<li>
						You need a general call centre agent for bookings,
						complaints or outbound sales. This is built for shopping
						from a catalog.
					</li>
					<li>
						You need it on a regular phone number on day one without
						any integration work. WhatsApp calls and in-app voice
						are the fastest start.
					</li>
					<li>
						You have no online catalog with prices and stock per
						branch.
					</li>
				</ul>
			</Article>

			<Section
				id="numbers-title"
				title="What the numbers say."
				lead="Only figures we can stand behind, each with its source."
			>
				<Figures items={FIGURES} />
			</Section>

			<Section id="steps-title" title="Live in three steps." center>
				<Steps items={STEPS} />
			</Section>

			<Faq items={FAQ} title="Voice ordering, answered." />
			<RelatedPages links={RELATED} />
			<Closing />
		</>
	);
}
